import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Loader2, SearchIcon, X } from "lucide-react";

interface SearchableSelectProps<T> {
  // Data
  data: T[];
  loading?: boolean;
  error?: string | null;
  value?: string;
  onChange: (value: string) => void;

  // Display functions
  getItemId: (item: T) => string;
  getDisplayText: (item: T) => string;

  // Optional: Custom search filter (if not provided, uses getDisplayText)
  filterItems?: (items: T[], searchTerm: string) => T[];

  // Optional: Pagination support
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;

  // UI Props
  label?: boolean | string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  showSearchIcon?: boolean;
  onDoubleClick?: React.MouseEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onSelect?: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
  size?: "default" | "spreadsheet";
  /** Spreadsheet size only: when disabled (prop or a disabled <fieldset>), show the value as
   *  plain text on the white cell — no grey box, no placeholder, no clear button. */
  plainWhenDisabled?: boolean;

  // Optional: Batch size for initial render (default 5)
  batchSize?: number;
}

function SearchableSelect<T>({
  data,
  loading = false,
  error = null,
  value,
  onChange,
  getItemId,
  getDisplayText,
  filterItems,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  label,
  placeholder,
  required = false,
  disabled = false,
  batchSize = 5,
  showSearchIcon = false,
  onDoubleClick,
  onBlur,
  onKeyDown,
  onSelect,
  inputRef: externalInputRef,
  size = "default",
  plainWhenDisabled = false,
}: SearchableSelectProps<T>) {
  const { t } = useTranslation();

  // Ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  // Detect dark mode for portal rendering
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains("dark")
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [visibleCount, setVisibleCount] = useState(batchSize);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const ignoreBlur = useRef(false);
  const isTypingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const shouldScrollIntoViewRef = useRef(false);
  const highlightedRef = useRef(highlighted);

  // Find selected item
  const selected = useMemo(
    () => safeData.find((item) => getItemId(item) === value),
    [safeData, value, getItemId]
  );

  // Filter items
  const filtered = useMemo(() => {
    if (filterItems) {
      // Use custom filter function
      return filterItems(safeData, searchTerm);
    } else {
      // Default filter: search in display text
      const term = searchTerm.toLowerCase();
      return term
        ? safeData.filter((item) =>
            getDisplayText(item).toLowerCase().includes(term)
          )
        : safeData;
    }
  }, [searchTerm, safeData, filterItems, getDisplayText]);

  // Visible items (for batch rendering)
  const visible = filtered.slice(0, visibleCount);

  // Reset visible count when search term or data changes
  useEffect(() => {
    setHighlighted(-1);
    setVisibleCount(batchSize);
  }, [searchTerm, safeData, batchSize]);

  // Sync search term from selected value (but not while user is typing)
  // This mirrors CachedPartnerSelect's value-watching effect
  useEffect(() => {
    if (isTypingRef.current) return;
    if (!selected) {
      setSearchTerm((prev) => (prev && value ? prev : ""));
      return;
    }
    const expectedText = getDisplayText(selected);
    setSearchTerm((prev) => (prev === expectedText ? prev : expectedText));
  }, [selected, value, getDisplayText]);

  // Reset typing flag when dropdown closes so value-watching effect re-syncs
  useEffect(() => {
    if (!isOpen) {
      isTypingRef.current = false;
    }
  }, [isOpen]);

  // Auto-highlight first result when dropdown opens or search results change
  useEffect(() => {
    if (isOpen && visible.length > 0) {
      const selectedIndex = filtered.findIndex((item) => getItemId(item) === value);

      if (selectedIndex >= 0) {
        // Ensure selected item is visible immediately when opening dropdown
        if (selectedIndex >= visibleCount) {
          const targetVisibleCount =
            Math.ceil((selectedIndex + 1) / batchSize) * batchSize;
          setVisibleCount(Math.min(targetVisibleCount, filtered.length));
        }
        setHighlighted(selectedIndex);
      } else {
        setHighlighted(0);
      }
    } else if (visible.length === 0) {
      setHighlighted(-1);
    }
  }, [isOpen, visible.length, filtered, getItemId, value, visibleCount, batchSize]);

  // Watch for dark mode changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // Calculate dropdown position when it opens.
  // getBoundingClientRect() is already viewport-relative, and the dropdown
  // uses position:fixed — so do NOT add window.scrollY/scrollX.
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        !inputRef.current?.contains(e.target as Node) &&
        !dropdownRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setHighlighted(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on page scroll or window resize so the portal stays anchored correctly.
  // Listen on window without capture so scrolling inside the dropdown list itself
  // does NOT close it — only scrolling the page background does.
  useEffect(() => {
    if (!isOpen) return;
    const close = () => { setIsOpen(false); setHighlighted(-1); };
    window.addEventListener("scroll", close, { passive: true });
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  // Keep ref in sync so open-scroll effect always reads the latest value
  useEffect(() => { highlightedRef.current = highlighted; }, [highlighted]);

  // Scroll highlighted item into view ONLY for keyboard navigation
  useEffect(() => {
    if (
      highlighted >= 0 &&
      dropdownRef.current &&
      shouldScrollIntoViewRef.current
    ) {
      const item = dropdownRef.current.children[highlighted] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
      shouldScrollIntoViewRef.current = false;
    }
  }, [highlighted]);

  // Scroll to selected item when dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (highlightedRef.current >= 0 && dropdownRef.current) {
        const item = dropdownRef.current.children[highlightedRef.current] as HTMLElement;
        item?.scrollIntoView({ block: "nearest" });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value;
    isTypingRef.current = true;
    setSearchTerm(newTerm);
    // Don't call onChange("") while typing — value is only cleared by the X button (handleClear).
    // Calling onChange("") here caused accidental value loss when user opened dropdown,
    // typed a character, then backspaced to empty.
    setIsOpen(true);
  };

  const handleSelect = (item: T) => {
    onChange(getItemId(item));
    setSearchTerm(getDisplayText(item));
    setIsOpen(false);
    isTypingRef.current = false;
    onSelect?.();
  };

  const handleClear = () => {
    // Send empty string to clear the value
    // Note: Parent components should handle "" as "no selection"
    onChange("");
    setSearchTerm("");
    setHighlighted(-1);
    setVisibleCount(batchSize);
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        shouldScrollIntoViewRef.current = true;
        setHighlighted((i) => Math.min(i + 1, visible.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        shouldScrollIntoViewRef.current = true;
        setHighlighted((i) => Math.max(i - 1, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlighted >= 0) handleSelect(visible[highlighted]);
        else if (visible.length === 1) handleSelect(visible[0]);
        break;
      case "Escape":
        setIsOpen(false);
        setHighlighted(-1);
        break;
    }
    onKeyDown?.(e);
  };

  const setInputRef = (element: HTMLInputElement | null) => {
    inputRef.current = element;
    if (typeof externalInputRef === "function") {
      externalInputRef(element);
    } else if (externalInputRef) {
      (externalInputRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
    }
  };

  const handleScroll = () => {
    const el = dropdownRef.current;
    if (!el) return;

    const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

    if (isNearBottom) {
      // If we have custom pagination (onLoadMore), use it
      if (onLoadMore && hasMore && !loading && !isLoadingMore) {
        onLoadMore();
      }
      // Otherwise use batch rendering
      else if (visibleCount < filtered.length) {
        setVisibleCount((v) => Math.min(v + batchSize, filtered.length));
      }
    }
  };

  // Render compact mode for inline use (no label)
  if (label === false) {
    const spreadsheet = size === "spreadsheet";
    return (
      <div className={spreadsheet ? "w-full h-6" : "w-full"}>
        <div ref={containerRef} className={`relative flex items-center ${spreadsheet ? "h-6" : ""}`}>
          {showSearchIcon && (
            <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none z-10" />
          )}
          <input
            ref={setInputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => !disabled && setIsOpen(true)}
            onClick={() => !disabled && setIsOpen(true)}
            onDoubleClick={onDoubleClick}
            onBlur={(event) => {
              if (!ignoreBlur.current) setIsOpen(false);
              onBlur?.(event);
            }}
            onKeyDown={handleInputKeyDown}
            className={spreadsheet ? `w-full h-6 text-sm border-0 outline-none bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${
              showSearchIcon ? "pl-5 pr-6" : "px-1 pr-6"
            } ${plainWhenDisabled
              ? "disabled:bg-transparent disabled:text-gray-800 disabled:opacity-100 disabled:cursor-default disabled:placeholder:text-transparent"
              : disabled ? "cursor-not-allowed bg-gray-100" : ""}` : `w-full h-10 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
              showSearchIcon ? "pl-7 pr-8" : "px-2 pr-8"
            } ${disabled ? "cursor-not-allowed bg-gray-50 dark:bg-gray-900" : ""}`}
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder}
          />
          {value && !disabled && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors z-10${plainWhenDisabled ? " disabled:hidden" : ""}`}
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}

          {isOpen && !disabled && dropdownPosition && createPortal(
            <div className={isDarkMode ? "dark" : ""}>
              <ul
                ref={dropdownRef}
                onMouseDown={() => (ignoreBlur.current = true)}
                onMouseUp={() => (ignoreBlur.current = false)}
                onScroll={handleScroll}
                className="fixed z-[9999] mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-y-auto max-h-[200px]"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                }}
                role="listbox"
              >
                {loading && visible.length === 0 ? (
                  <li className="px-4 py-3 text-gray-500 dark:text-gray-400 flex items-center justify-center">
                    <Loader2 className="animate-spin h-5 w-5 text-blue-500 mr-2" />
                  </li>
                ) : error ? (
                  <li className="px-4 py-3 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">
                    {error}
                  </li>
                ) : visible.length === 0 ? (
                  <li className="px-4 py-3 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700">
                    {t("forms.noResults")}
                  </li>
                ) : (
                  <>
                    {visible.map((item, index) => {
                      const itemId = getItemId(item);
                      return (
                        <li
                          key={itemId}
                          className={`px-4 py-2.5 cursor-pointer transition-colors ${
                            highlighted === index
                              ? value === itemId
                                ? "bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500"
                                : "bg-blue-50 dark:bg-blue-900/30"
                              : value === itemId
                              ? "border-l-4 border-blue-500"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setHighlighted(index)}
                          role="option"
                          aria-selected={value === itemId}
                          id={`select-item-${index}`}
                        >
                          <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                            {getDisplayText(item)}
                          </div>
                        </li>
                      );
                    })}
                    {isLoadingMore && (
                      <li className="px-4 py-2 text-gray-500 dark:text-gray-400 flex items-center justify-center">
                        <Loader2 className="animate-spin h-4 w-4 text-blue-500" />
                      </li>
                    )}
                  </>
                )}
              </ul>
            </div>,
            document.body
          )}
        </div>
      </div>
    );
  }

  // Full mode with proper styling
  return (
    <div className="w-full">
      <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">
        {typeof label === "string" ? label : placeholder || "Select"}
        {required && <span className="text-red-500">*</span>}
      </label>

      <div ref={containerRef} className="relative">
        <div
          className={`flex items-center border rounded-lg bg-white dark:bg-gray-800 h-10 transition-all duration-200 ${
            disabled
              ? "cursor-not-allowed bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
              : "cursor-text"
          } ${
            isOpen && !disabled
              ? "border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/50"
              : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
          }`}
          onClick={() => !disabled && inputRef.current?.focus()}
        >
          <div className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
            <SearchIcon size={14} />
          </div>

          <input
            ref={setInputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => !disabled && setIsOpen(true)}
            onClick={() => !disabled && setIsOpen(true)}
            onDoubleClick={onDoubleClick}
            onBlur={(event) => {
              if (!ignoreBlur.current) {
                // Blur validation (same pattern as CachedPartnerSelect):
                // If user typed something that doesn't match any item and no value
                // is committed, clear the input so it doesn't show stale text.
                const matchesExisting = filtered.some(
                  (item) => getDisplayText(item).toLowerCase() === searchTerm.toLowerCase()
                );
                if (!matchesExisting && searchTerm && !value) {
                  setSearchTerm("");
                }
                setIsOpen(false);
              }
              onBlur?.(event);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            className="w-full py-1.5 pl-8 pr-8 text-sm bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
            autoComplete="off"
            disabled={disabled}
          />

          {(value || searchTerm) && !disabled && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors text-xl leading-none"
              title="Clear"
            >
              ×
            </button>
          )}
        </div>

        {isOpen && !disabled && dropdownPosition && createPortal(
          <div className={isDarkMode ? "dark" : ""}>
            <ul
              ref={dropdownRef}
              onMouseDown={() => (ignoreBlur.current = true)}
              onMouseUp={() => (ignoreBlur.current = false)}
              onScroll={handleScroll}
              className="fixed z-[9999] mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-y-auto max-h-[200px]"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
              }}
              role="listbox"
            >
              {loading && visible.length === 0 ? (
                <li className="px-4 py-3 text-gray-500 dark:text-gray-400 flex items-center justify-center">
                  <Loader2 className="animate-spin h-5 w-5 text-blue-500 mr-2" />
                </li>
              ) : error ? (
                <li className="px-4 py-3 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">
                  {error}
                </li>
              ) : visible.length === 0 ? (
                <li className="px-4 py-3 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700">
                  {t("forms.noResults")}
                </li>
              ) : (
                <>
                  {visible.map((item, index) => {
                    const itemId = getItemId(item);
                    return (
                      <li
                        key={itemId}
                        className={`px-4 py-2.5 cursor-pointer transition-colors ${
                          highlighted === index
                            ? value === itemId
                              ? "bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500"
                              : "bg-blue-50 dark:bg-blue-900/30"
                            : value === itemId
                            ? "border-l-4 border-blue-500"
                            : "hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setHighlighted(index)}
                        role="option"
                        aria-selected={value === itemId}
                        id={`select-item-${index}`}
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {getDisplayText(item)}
                        </div>
                      </li>
                    );
                  })}
                  {isLoadingMore && (
                    <li className="px-4 py-2 text-gray-500 dark:text-gray-400 flex items-center justify-center">
                      <Loader2 className="animate-spin h-4 w-4 text-blue-500" />
                    </li>
                  )}
                </>
              )}
            </ul>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}

export default SearchableSelect;
